import Icon from '../ui/Icon';
import Spinner from '../ui/Spinner';

/** Human-readable summary of an applied coupon. */
function describeCoupon(coupon) {
  if (coupon.type !== 'stripe') return coupon.description;

  const amount = coupon.percent_off
    ? `${coupon.percent_off}% off`
    : `$${(coupon.amount_off / 100).toFixed(2)} off`;
  return `${coupon.description} - ${amount}`;
}

/**
 * Coupon entry with three states: collapsed link, open input, and applied confirmation.
 *
 * The coupon error is rendered inline (role="alert"), which is the pattern the rest of the
 * form should follow -- submit failures still use alert() and get fixed in a later step.
 *
 * @param {object} props
 * @param {string} props.code
 * @param {?object} props.applied         null, or {type, description, percent_off?, amount_off?}
 * @param {string} props.error
 * @param {boolean} props.validating
 * @param {boolean} props.expanded
 * @param {Function} props.onCodeChange   (value) => void
 * @param {Function} props.onApply
 * @param {Function} props.onRemove
 * @param {Function} props.onExpand
 * @param {Function} props.onCancel
 */
export default function CouponField({
  code,
  applied,
  error,
  validating,
  expanded,
  onCodeChange,
  onApply,
  onRemove,
  onExpand,
  onCancel,
}) {
  if (applied) {
    return (
      <div className="mb-4">
        <div
          role="status"
          className="bg-success-50 border border-success-200 rounded-sm p-3 flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2">
            <Icon name="checkCircle" className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span className="text-sm font-medium text-success-800">
              Coupon applied: {describeCoupon(applied)}
            </span>
          </div>
          <button
            type="button"
            onClick={onRemove}
            // p-2 to get closer to the 44px tap-target guidance; it was text-only before.
            className="p-2 -m-1 text-success-700 hover:text-success-900 text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-success-500 flex-shrink-0"
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  if (!expanded) {
    return (
      <div className="mb-4">
        <button
          type="button"
          onClick={onExpand}
          // primary-700: primary-600 (#ea580c) on white is ~3.6:1 and fails WCAG AA at this
          // text size.
          className="text-primary-700 hover:text-primary-800 text-sm font-medium flex items-center gap-1 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <Icon name="tag" className="w-4 h-4" />
          Have a coupon code?
        </button>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <label htmlFor="couponCode" className="sr-only">
            Coupon code
          </label>
          <input
            id="couponCode"
            type="text"
            value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            placeholder="Enter coupon code"
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? 'couponCode-error' : undefined}
            className="flex-1 rounded-sm border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm px-3 py-2"
            disabled={validating}
          />
          <button
            type="button"
            onClick={onApply}
            disabled={validating || !code.trim()}
            aria-busy={validating || undefined}
            className="px-4 py-3 sm:py-2 bg-primary-600 text-white text-sm font-medium rounded-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto flex items-center justify-center"
          >
            {validating ? <Spinner /> : 'Apply'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-3 sm:py-2 text-gray-600 hover:text-gray-800 text-sm w-full sm:w-auto rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Cancel
          </button>
        </div>

        {error && (
          <div id="couponCode-error" role="alert" className="text-danger-600 text-sm">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
